import { Box, Card, CardActions, Grid, Link, ListItemIcon, ListItemText, Menu, MenuItem, Tab, Tabs, Typography, type SxProps } from "@mui/material";
import * as React from "react";
import { useCreatePath } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { BrandIcon } from "../../components";
import { BrandButton } from "../../components/brand-button";
import { Carousel } from "../../components/carousel/Carousel";
import { useIsSmall } from "../../hooks";
import { fullName, productFullName, type Offer, type OfferDetail, type OfferVariant } from "../../models";
import { type Currency } from "../../models/currency";
import type { ValuesOf } from "../../models/enums";
import { rgbToHex } from "../../util";
import { Add } from "./add";

interface OfferCardProps {
    offer: Offer;
    currency: ValuesOf<typeof Currency>;
    taxRate: number;
    sx?: SxProps;
    fontSize?: string;
    offerIndex: [number, React.Dispatch<React.SetStateAction<number>>];
    detailIndex: [number, React.Dispatch<React.SetStateAction<number>>];
    expandedIndex?: [number, React.Dispatch<React.SetStateAction<number>>];
}

export const OfferCard = ({ offer, taxRate, currency, offerIndex, detailIndex, expandedIndex, sx, fontSize = "1.4rem" }: OfferCardProps): JSX.Element => {
    const [offerIdx, setOfferIdx] = offerIndex;
    const [detailIdx, setDetailIdx] = detailIndex;
    const variant = offer.variants[offerIdx];
    const detail = variant.details[detailIdx];
    const createPath = useCreatePath();
    const path = createPath({
        resource: "shop",
        type: "edit",
        id: offer.id,
    });

    const items = (detail.images.length > 0 ? detail.images : [detail.image!]).map((x, i) => ({
        src: x,
        title: `${productFullName({ brand: detail.brand, name: detail.product, variant: detail.variant })}${i}`,
    }));

    return (
        <Card sx={{ boxShadow: 2, overflow: "hidden", position: "relative" }}>
            <CardBorder x="right" y="top"></CardBorder>
            {/* <Ribbon>
                New
            </Ribbon> */}
            <Grid container sx={{ minHeight: 255 }}>
                <CardHeader offer={offer} detail={detail} variant={variant} setDetailIdx={setDetailIdx} expandedIndex={expandedIndex} path={path} fontSize={fontSize} />
                <Grid size={{ xs: 12 }} sx={{ paddingTop: "1px", flexGrow: 1 }}>
                    <Carousel items={items} sx={sx} container={x => <Link component={RouterLink} to={path} {...x} />} />
                </Grid>
            </Grid>
            <CardActions
                disableSpacing
                sx={{
                    padding: 0,
                }}
            >
                {offer.variants.length ? (
                    <Tabs
                        scrollButtons="auto"
                        // sx={{ paddingTop: "6px" }}
                        textColor="secondary"
                        value={offerIdx}
                        onChange={(_, val) => {
                            setOfferIdx(val);
                            setDetailIdx(0);
                            if (expandedIndex) {
                                expandedIndex[1](0);
                            }
                        }}
                    >
                        {offer.variants.map(o => (
                            <Tab label={o.name} />
                        ))}
                    </Tabs>
                ) : (
                    <></>
                )}
                <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    {/* <Rating
                        size="small"
                        value={(detail.rating ?? 0) * 5}
                        readOnly
                    /> */}
                </Box>
                {/* polygon(30% 0%, 50% 100%, 0% 100%, 0% 0%) */}
                <Box>
                    <Add amount={variant.price * (1 + taxRate)} currency={currency} id={variant.id} />
                </Box>
            </CardActions>
        </Card>
    );
};

function CardHeader({ detail, variant, expandedIndex, setDetailIdx, path, fontSize, offer }: { detail: OfferDetail, setDetailIdx: React.Dispatch<React.SetStateAction<number>>, variant: OfferVariant, expandedIndex: [number, React.Dispatch<React.SetStateAction<number>>] | undefined, path: string, fontSize: string, offer: Offer }) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const isSmall = useIsSmall();
    return <Grid size={{ xs: 12 }} sx={{ display: "flex", alignItems: "center" }}>
        <BrandButton
            image={{
                title: detail.brand,
                src: detail.brandImage ?? "",
                color: detail.brandColor,
            }}
            multiple={variant.details.length > 1}
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={variant.details.length > 1 ? handleClick : undefined}
        >{detail.brand}</BrandButton>
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
                list: {
                    "aria-labelledby": "basic-button",
                },
            }}
        >
            {variant.details.map((x, i) => (
                <MenuItem
                    key={x.id}
                    onClick={() => {
                        setDetailIdx(i);
                        if (expandedIndex) {
                            expandedIndex[1](i + 1);
                        }
                        handleClose();
                    } }
                >
                    <ListItemIcon>
                        <BrandIcon color={x.brandColor ? rgbToHex(x.brandColor) : undefined} image={{ src: x.brandImage, title: x.brand }} />
                    </ListItemIcon>
                    <ListItemText sx={{ marginLeft: 1 }}>{fullName({ name: x.product, variant: x.variant })}</ListItemText>
                </MenuItem>
            ))}
        </Menu>
        <Box sx={{ paddingLeft: 1, position: "relative", flex: 1 }}>
            <Link
                color="primary"
                component={RouterLink}
                to={path}
                underline="none"
                sx={{
                    display: "block",
                    whiteSpace: "nowrap",
                    maxWidth: isSmall ? 290 : "100%",
                    overflow: "clip",
                    fontSize,
                } as SxProps}
            >
                {offer.name}
            </Link>
            {variant.details.length > 1 ? (
                <Typography
                    color="textSecondary"
                    sx={{
                        position: "absolute",
                        bottom: -12,
                        left: 10,
                        fontSize: "0.7rem",
                    }}
                >
                    {fullName({ name: detail.product, variant: detail.variant })}
                </Typography>
            ) : (
                <></>
            )}
        </Box>
    </Grid>;
}

const CardBorder = ({ x, y, children }: { x: "left" | "right", y: "top" | "bottom" } & React.PropsWithChildren) => {
    return (
        <Box
            sx={(t) => ({
                // [`border${x.charAt(0).toUpperCase() + x.slice(1)}`]: `solid 3px ${t.palette.primary.main}`,
                [`border${y.charAt(0).toUpperCase() + y.slice(1)}`]: `solid 3px ${t.palette.primary.main}`,
                position: "absolute",
                [x]: 0,
                [y]: 0,
                width: 90,
                height: 48,
                zIndex: "2",
                paddingTop: 1,
                textAlign: "center"
            })}
        >{children}</Box>
    );
};